import { Grid } from "antd";
import { useMemo } from "react";
import ReactApexChart from "react-apexcharts";

const { useBreakpoint } = Grid;

export const ChartV2 = ({
  series,
  chartMax,
  chartMin,
  height,
  separateBars,
  hideLegends,
  barHeight,
}) => {
  const screens = useBreakpoint();
  // Convert HH:MM:SS → seconds
  const timeToSeconds = (t) => {
    if (!t || typeof t !== "string") return null;

    const [h, m, s] = t?.split(":").map(Number);
    return h * 3600 + m * 60 + s;
  };

  const minSec = timeToSeconds(chartMin);
  const maxSec = timeToSeconds(chartMax);

  // Prepare series for ApexCharts (relative seconds)
  const convertedSeries = useMemo(
    () =>
      separateBars
        ? series[0].data.map((item) => {
            const startSec = timeToSeconds(item.y[0]);
            const endSec = timeToSeconds(item.y[1]);

            return {
              name: item.x, // legend name
              data: [
                {
                  ...item,
                  y: [startSec - minSec, endSec - minSec],
                  _meta: {
                    startRaw: item.y[0],
                    endRaw: item.y[1],
                    duration: item.duration,
                  },
                },
              ],
            };
          })
        : series.map((group) => ({
            ...group,
            data: group.data.map((item) => {
              const startSec = timeToSeconds(item.y[0]);
              const endSec = timeToSeconds(item.y[1]);

              return {
                ...item,
                y: [startSec - minSec, endSec - minSec], // relative seconds from min
                _meta: {
                  startRaw: item.y[0],
                  endRaw: item.y[1],
                  duration: item.duration,
                },
              };
            }),
          })),
    [series],
  );

  const seriesColors = convertedSeries.map(
    (s) => s.data[0]?.fillColor || "#000000",
  );

  return (
    <ReactApexChart
      type="rangeBar"
      height={height}
      series={convertedSeries}
      options={{
        grid: {
          padding: {
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
          },
        },
        chart: { type: "rangeBar", height },
        plotOptions: {
          bar: {
            horizontal: true,
            rangeBarGroupRows: true,
            borderRadius: 6,
            barHeight,
          },
        },
        colors: seriesColors,

        // X-axis in relative seconds
        xaxis: {
          type: "numeric",
          min: 0,
          max: maxSec - minSec, // MUST set this for correct max
          tickAmount: Math.ceil((maxSec - minSec) / 5),
          labels: {
            formatter: (val) => val?.toFixed(1) + "s",
            style: {
              fontSize: screens.xxl ? "12px" : "10px",
            },
          },
          title: {
            text: "Seconds",
            style: {
              fontSize: screens.xxl ? "12px" : "10px",
            },
          },
        },

        yaxis: {
          show: false,
        },

        dataLabels: {
          enabled: true,
          formatter: (val, opts) => {
            const duration =
              opts.w.config.series[opts.seriesIndex].data[opts.dataPointIndex]
                ._meta.duration;
            return duration?.toFixed(1) + "s";
          },
          style: {
            fontSize: screens.xxl ? "12px" : "10px",
          },
        },

        tooltip: {
          shared: false,
          custom: ({ seriesIndex, dataPointIndex, w }) => {
            const point =
              w.config.series[seriesIndex].data[dataPointIndex]._meta;
            return `
              <div style="padding:6px 10px;">
                <strong>${w.globals.seriesNames[seriesIndex]}</strong><br/>
                Start: ${point.startRaw}<br/>
                End: ${point.endRaw}<br/>
                Duration: ${point.duration?.toFixed(1)}s
              </div>
            `;
          },
        },

        legend: {
          show: hideLegends ? false : true,
          position: "top",
          horizontalAlign: screens.xxl ? "center" : "left",
          fontSize: screens.xxl ? "12px" : "10px",
        },
      }}
    />
  );
};
